import EHIModal from '../../../../react/components/ehi-modal/ehi-modal.js';
import Vimeo from '../javascripts/vimeo-player-2.25.0.js';

export default class VimeoModal extends EHIModal {
    player;
    activeTrigger;

    constructor(props) {
        super(props);

        this.config.id = 'ehi-modal';
        this.config.modalClass = `vimeo-modal vimeo-card-with-modal modal-${this.data.node}`;
        this.config.destroyOnCancel = false;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.vimeoData !== this.props.vimeoData) {
            this.config.modalBodyContent = null;

            if (this.bodyRef.current) {
                this.bodyRef.current.innerHTML = '';
            }

            this.mountModalBodyContent();
        }
    }

    afterComponentDidMount() {
        document.body.addEventListener('click', this.onBodyClick);
    }

    afterComponentWillUnmount() {
        document.body.removeEventListener('click', this.onBodyClick);
    }

    async mountModalBodyContent() {
        if (!this.bodyRef.current) {
            return;
        }

        const content = await this.getModalBodyContent();

        if (typeof content === 'string') {
            this.bodyRef.current.innerHTML = content;
        } else {
            this.bodyRef.current.appendChild(content);
        }
    }

    isEditor() {
        return (window.FLBuilder && document.body.classList.contains('fl-builder-edit') && new URLSearchParams(window.location.search).has('fl_builder'));
    }

    hasAnalyticsConsent() {
        return (typeof Cookiebot !== 'undefined') ? Cookiebot.consent?.statistics : true;
    }
    
    get data() {
        return this.props.data;
    }

    get vimeoData() {
        return this.props.vimeoData;
    }

    async getModalBodyContent() {
        if (this.config.modalBodyContent) {
            return this.config.modalBodyContent;
        };

        const vimeoData = this.vimeoData;
        const vimeoEl = document.createElement('div');

        if (vimeoData?.html) {
            const ratio = Math.round((vimeoData.width / vimeoData.height) * 100);
            const ratioClass = (ratio == 178) ? 'ratio-16x9' : 'ratio-4x3';

            const iframe = new DOMParser().parseFromString(vimeoData.html, 'text/html').body.firstElementChild;
            iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');

            const allow = iframe.getAttribute('allow') || '';
            iframe.setAttribute('allow', (allow) ? `${allow}; presentation` : 'presentation');

            vimeoEl.classList.add('vimeo-container', ratioClass);
            vimeoEl.appendChild(iframe);

            this.player = (iframe) ? new Vimeo(iframe) : null;

            if ((this.data.settings.video_events_active ?? null == 'yes') && window.dataLayer && this.hasAnalyticsConsent()) {
                this.loadAnalytics();
            }
        } else {
            vimeoEl.classList.add('video-not-available');
            vimeoEl.insertAdjacentHTML('beforeend', this.data.settings.video_not_available_message);
        }

        this.config.modalBodyContent = vimeoEl;
        return vimeoEl;
    }

    formatSeconds(seconds) {
        const totalSeconds = Math.floor(seconds);

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        return [
            String(hours).padStart(2, '0'),
            String(minutes).padStart(2, '0'),
            String(secs).padStart(2, '0')
        ].join(':');
    }

    loadAnalytics() {
        if (!this.player || typeof window.dataLayer == 'undefined') {
            return;
        }

        const analyticsData = {
            id: this.data.node,
            label: this.data.settings.analytics_label
                ? this.data.settings.analytics_label
                : this.vimeoData.title ?? `Vimeo ID: ${this.data.settings.video_id}`,
            pageUrl: location.href
        };

        const watchEvents = {
            25: false,
            50: false,
            75: false,
            95: false,
            100: false
        };

        let lastPercent = 0;
        let seeking = false;
        let isPlaying = false;
        let hasPlayed = false;

        this.player.on('play', async () => {
            isPlaying = true;

            const current = (await this.player.getCurrentTime?.()) || 0;

            // normalize baseline percent
            const duration = await this.player.getDuration?.();

            lastPercent = (current / duration) * 100 || 0;

            if (!hasPlayed) {
                hasPlayed = true;

                if (this.hasAnalyticsConsent()) {
                    window.dataLayer.push({
                        event: 'vimeoPlay',
                        ...analyticsData
                    });
                }
            }
        });

        this.player.on('seeked', (data) => {
            const percent = data.percent * 100;

            isSeeking = true;

            // mark milestones as already reached WITHOUT firing events
            for (const pct of Object.keys(watchEvents)) {
                if (percent >= pct) {
                    watchEvents[pct] = true;
                }
            }

            lastPercent = percent;

            setTimeout(() => {
                isSeeking = false;
            }, 200);
        });

        this.player.on('timeupdate', (data) => {
            if (!isPlaying || isSeeking) {
                return;
            }

            const percent = data.percent * 100;

            for (const pct of Object.keys(watchEvents)) {

                if (this.hasAnalyticsConsent() && !watchEvents[pct] && lastPercent < pct && percent >= pct) {
                    window.dataLayer.push({
                        event: 'vimeoWatched',
                        completedPct: Number(pct),
                        ...analyticsData
                    });

                    watchEvents[pct] = true;
                }
            }

            lastPercent = percent;
        });

        this.player.on('ended', () => {
            if (watchEvents[100]) {
                return;
            }

            if (this.hasAnalyticsConsent()) {
                window.dataLayer.push({
                    event: 'vimeoWatched',
                    completedPct: 100,
                    ...analyticsData
                });
            }

            watchEvents[100] = true;
        });
    }

    onAfterCancelButtonClick = (event) => {
		if (event instanceof Object === false) {
			return;
		}
        
        this.setState({ active: false });

        if (this.activeTrigger) {
            this.activeTrigger.focus();
        }

        if (this.player) {
            this.player.pause();
        }
	}

    onBodyClick = (event) => {
        if (!event.target.matches(`[data-lum-vimeo-card-modal="${this.data.settings.modal_id}"]`)) {
            return;
        }

        event.preventDefault();

        this.activeTrigger = event.target;

        this.setState(prev => ({ active: !prev.active }));
    }
}